/**
 * ReportingTab — charts + reason table + recent events for user profile.
 *
 * Reuses V1 chart components: HourlyLineAndBarChart, LineTrendingChart.
 * Displays event reason breakdown and recent events.
 */

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import HourlyLineAndBarChart from '../../../../components/charts/HourlyLineAndBarChart.jsx';
import LineTrendingChart from '../../../../components/charts/LineTrendingChart.jsx';
import CallsCell from '../../../../components/v2/CallsCell.jsx';
import { convertIsoToLocal } from '../../../../libs.jsx';

const ReportingTab = ({
    hourData, weekData, reasonData, eventData,
    eventNotesActive, isGrandstream, tzOffset, onAddNote, userId,
}) => {
    const reasonRows = useMemo(() => {
        if (Array.isArray(reasonData?.reasons)) return reasonData.reasons;
        if (Array.isArray(reasonData)) return reasonData;
        return [];
    }, [reasonData]);

    const weekChartProps = useMemo(() => ({
        lineData: [weekData?.events?.length > 0 ? weekData.events : []],
        lineColors: ['#0081C7'],
        lineLabels: ['Total Alarms'],
        axisLabels: weekData?.labels?.length > 0 ? weekData.labels : [],
    }), [weekData]);

    const getDeviceLink = (evt) => {
        const mfg = parseInt(evt.manufacture_id);
        if (mfg === 4) return `/community/devices/4/${evt.device_id}`;
        return `/community/devices/1/${evt.device_id}`;
    };

    return (
        <>
            {/* Charts */}
            {/* Report action */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginBottom: 'var(--space-3)' }}>
                <Link to="/reports/alerts/run" state={{ filters: { users: [userId] } }}>
                    <button className="dp-btn dp-btn--secondary dp-btn--sm">Run Detailed Report &rarr;</button>
                </Link>
            </div>

            {/* Charts — separate cards in a responsive grid */}
            <div className="ip-chart-row" style={{ marginBottom: 'var(--space-4)' }}>
                <div className="pp-section">
                    <div className="pp-section__header">
                        <h3 className="pp-section__title">Alarm Activity by Hour</h3>
                    </div>
                    <div className="pp-section__body">
                        <div className="ip-chart-section">
                            <HourlyLineAndBarChart
                                style={{ maxHeight: '350px' }}
                                labels={hourData?.labels || Array.from({ length: 24 }, (_, i) => String(i))}
                                x_axis={true} x_axis_title="Time of the Day"
                                bar_axis={true} bar_axis_title="Total Alarms" bar_color="#0081C7"
                                line_axis={true} line_axis_title="Average Response Time" line_color="#BE4D25"
                                bar_data={hourData?.events || []}
                                line_data={hourData?.responses || []}
                                bar_annotation={true}
                                bar_annotation_value={hourData?.avgValue || null}
                                bar_annotation_label="Average"
                            />
                        </div>
                    </div>
                </div>

                <div className="pp-section">
                    <div className="pp-section__header">
                        <h3 className="pp-section__title">Weekly Alarm Trend</h3>
                    </div>
                    <div className="pp-section__body">
                        <div className="ip-chart-section">
                            <LineTrendingChart
                                style={{ maxHeight: '350px' }}
                                x_axis={true}
                                x_axis_title="Weeks"
                                line_axis={true}
                                line_axis_title="Total Alarms"
                                lines={1}
                                line_color={weekChartProps.lineColors}
                                line_labels={weekChartProps.lineLabels}
                                line_data={weekChartProps.lineData}
                                forceAspect={true}
                                labels={weekChartProps.axisLabels}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Event Reason Breakdown + Recent Events (always shown, side-by-side) */}
            <div className="ip-report-grid">
                {/* Event Reason Breakdown */}
                <div className="pp-section">
                    <div className="pp-section__header">
                        <h3 className="pp-section__title">Event Reason Breakdown</h3>
                    </div>
                    <div className="pp-section__body pp-section__body--flush">
                        <div className="dp-table-wrap">
                            <table className="dp-table">
                                <thead className="dp-thead">
                                    <tr>
                                        <th className="dp-th">Reason</th>
                                        <th className="dp-th" style={{ textAlign: 'center' }}>Total</th>
                                        <th className="dp-th" style={{ textAlign: 'center' }}>% Total Volume</th>
                                        <th className="dp-th" style={{ textAlign: 'center' }}>Avg Response (min)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr className="dp-tr ip-reason-total">
                                        <td className="dp-td"><span className="dp-cell-text" style={{ fontWeight: 600 }}>Total Events</span></td>
                                        <td className="dp-td" style={{ textAlign: 'center' }}><span className="dp-cell-text" style={{ fontWeight: 600 }}>{reasonData?.count || 0}</span></td>
                                        <td className="dp-td"></td>
                                        <td className="dp-td" style={{ textAlign: 'center' }}><span className="dp-cell-text" style={{ fontWeight: 600 }}>{reasonData?.avg_response_time || '0.0'}</span></td>
                                    </tr>
                                    {reasonRows.map((row, idx) => (
                                        <tr key={idx} className="dp-tr">
                                            <td className="dp-td"><span className="dp-cell-text">{row.reason_name || row.reason_code_description || '—'}</span></td>
                                            <td className="dp-td" style={{ textAlign: 'center' }}><span className="dp-cell-text">{row.count || 0}</span></td>
                                            <td className="dp-td">
                                                <div className="ip-percent-bar">
                                                    <div className="ip-percent-bar__track">
                                                        <div className="ip-percent-bar__fill" style={{ width: `${parseFloat(row.percent) || 0}%` }} />
                                                    </div>
                                                    <span className="ip-percent-bar__label">{row.percent || '—'}</span>
                                                </div>
                                            </td>
                                            <td className="dp-td" style={{ textAlign: 'center' }}><span className="dp-cell-text">{row.avg_response_time || '—'}</span></td>
                                        </tr>
                                    ))}
                                    {reasonRows.length === 0 ? (
                                        <tr className="dp-tr">
                                            <td className="dp-td" colSpan={4} style={{ textAlign: 'center' }}>
                                                <span className="dp-cell-text dp-cell-text--muted">No event data for this period</span>
                                            </td>
                                        </tr>
                                    ) : null}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Recent Events */}
                <div className="pp-section">
                    <div className="pp-section__header">
                        <h3 className="pp-section__title">Recent Events</h3>
                    </div>
                    <div className="pp-section__body pp-section__body--flush" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        <div className="dp-table-wrap">
                            <table className="dp-table">
                                <thead className="dp-thead">
                                    <tr>
                                        <th className="dp-th">Event Start</th>
                                        <th className="dp-th" style={{ textAlign: 'center' }}>Resp (min)</th>
                                        <th className="dp-th">Device</th>
                                        <th className="dp-th">Resident</th>
                                        <th className="dp-th">Area</th>
                                        <th className="dp-th" style={{ textAlign: 'center' }}>Reason</th>
                                        {eventNotesActive ? <th className="dp-th" style={{ textAlign: 'center' }}>Notes</th> : null}
                                        {isGrandstream ? <th className="dp-th" style={{ textAlign: 'center' }}>Calls</th> : null}
                                    </tr>
                                </thead>
                                <tbody>
                                    {(eventData || []).map((evt, idx) => (
                                        <tr key={evt.event_id || idx} className="dp-tr">
                                            <td className="dp-td">
                                                <Link to={`/profiles/event/${evt.event_id}`} className="pp-field__value--link" style={{ fontSize: 'var(--font-size-sm)' }}>
                                                    {evt.event_datetime_local ? convertIsoToLocal(evt.event_datetime_local, tzOffset) : '—'}
                                                </Link>
                                            </td>
                                            <td className="dp-td" style={{ textAlign: 'center' }}>
                                                <span className="dp-cell-text">{evt.response_time ?? '—'}</span>
                                            </td>
                                            <td className="dp-td">
                                                {evt.device_id ? (
                                                    <Link to={getDeviceLink(evt)} className="pp-field__value--link" style={{ fontSize: 'var(--font-size-sm)' }}>
                                                        <span>{evt.description || '—'}</span>
                                                        {evt.manufacture_uuid ? (
                                                            <span className="dp-cell-text dp-cell-text--muted" style={{ display: 'block', fontSize: 'var(--font-size-xs)' }}>{evt.manufacture_uuid}</span>
                                                        ) : null}
                                                    </Link>
                                                ) : (<span className="dp-cell-text dp-cell-text--muted">—</span>)}
                                            </td>
                                            <td className="dp-td">
                                                {evt.resident_id ? (
                                                    <Link to={`/profiles/resident/${evt.resident_id}`} className="pp-field__value--link" style={{ fontSize: 'var(--font-size-sm)' }}>{evt.resident_name || '—'}</Link>
                                                ) : (<span className="dp-cell-text dp-cell-text--muted">—</span>)}
                                            </td>
                                            <td className="dp-td">
                                                {evt.area_id ? (
                                                    <Link to={`/profiles/area/${evt.area_id}`} className="pp-field__value--link" style={{ fontSize: 'var(--font-size-sm)' }}>{evt.area_name || '—'}</Link>
                                                ) : (<span className="dp-cell-text dp-cell-text--muted">—</span>)}
                                            </td>
                                            <td className="dp-td" style={{ textAlign: 'center' }}>
                                                <span className="dp-cell-text">{evt.reason_code_description || '—'}</span>
                                            </td>
                                            {eventNotesActive ? (
                                                <td className="dp-td" style={{ textAlign: 'center' }}>
                                                    <button className="dp-btn dp-btn--secondary dp-btn--xs" onClick={() => onAddNote(evt, idx)} title="Add Note">
                                                        {evt.notes?.length > 0 ? `${evt.notes.length}` : '+'} Note{evt.notes?.length !== 1 ? 's' : ''}
                                                    </button>
                                                </td>
                                            ) : null}
                                            {isGrandstream ? (
                                                <td className="dp-td" style={{ textAlign: 'center' }}>
                                                    <CallsCell
                                                        calls={evt.calls}
                                                        tzOffset={tzOffset}
                                                        ariaPrefix={`Event ${evt.event_id || ''}`}
                                                    />
                                                </td>
                                            ) : null}
                                        </tr>
                                    ))}
                                    {(eventData || []).length === 0 ? (
                                        <tr className="dp-tr">
                                            <td className="dp-td" colSpan={6 + (eventNotesActive ? 1 : 0) + (isGrandstream ? 1 : 0)} style={{ textAlign: 'center' }}>
                                                <span className="dp-cell-text dp-cell-text--muted">No recent events</span>
                                            </td>
                                        </tr>
                                    ) : null}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ReportingTab;
